import { useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from '@/components/ui/sonner';
import { UploadCloud, Loader2, Check, X, Trash2, ChevronsUpDown, ImageOff } from 'lucide-react';

const ALLOWED = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Filename heuristic: match by GTIN/SKU or product title tokens.
const guessProduct = (filename, products) => {
  const base = norm(filename.replace(/\.[^.]+$/, ''));
  if (!base) return '';
  let best = '';
  for (const p of products) {
    const sku = norm(p.gtin_sku_global);
    if (sku && sku.length >= 3 && (base.includes(sku) || sku.includes(base))) return p.id;
    const title = norm(p.title);
    if (title && title.length >= 5 && (base.includes(title) || title.includes(base))) best = best || p.id;
  }
  return best;
};

const ProductPicker = ({ products, value, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const selected = products.find((p) => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} data-testid={`${testid}-trigger`} className="w-full justify-between font-normal">
          <span className="truncate">{selected ? selected.title : 'Sin asignar'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command filter={(val, search) => (val.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder="Buscar por nombre, marca o SKU" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>Sin coincidencias.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__none__ sin asignar" onSelect={() => { onChange(''); setOpen(false); }} data-testid={`${testid}-none`}>
                <Check className={`mr-2 h-4 w-4 ${!value ? 'opacity-100' : 'opacity-0'}`} /> Sin asignar
              </CommandItem>
              {products.map((p) => (
                <CommandItem key={p.id} value={`${p.title} ${p.brand} ${p.gtin_sku_global}`} onSelect={() => { onChange(p.id); setOpen(false); }} data-testid={`${testid}-option-${p.id}`}>
                  <Check className={`mr-2 h-4 w-4 ${value === p.id ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="flex flex-col"><span className="truncate">{p.title}</span><span className="text-xs text-muted-foreground">{p.gtin_sku_global}{p.image_url ? ' · ya tiene imagen' : ''}</span></span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export const BulkImageUpload = ({ products, onClose, onDone }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const availableProducts = useMemo(() => products.filter((p) => p.active !== false), [products]);
  const matchedCount = items.filter((it) => it.productId && it.status !== 'done').length;

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    const rows = [];
    for (const f of files) {
      if (!ALLOWED.includes(f.type)) { toast.error(`${f.name}: formato no admitido`); continue; }
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: supera 8 MB`); continue; }
      const preview = await fileToBase64(f);
      rows.push({ id: crypto.randomUUID(), file: f, name: f.name, preview, productId: guessProduct(f.name, availableProducts), status: 'pending', error: '' });
    }
    if (rows.length) setItems((prev) => [...prev, ...rows]);
  };

  const onDrop = (e) => { e.preventDefault(); if (!busy) addFiles(e.dataTransfer.files); };
  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));
  const setProduct = (id, productId) => setItems((prev) => prev.map((it) => (it.id === id ? { ...it, productId } : it)));

  const apply = async () => {
    if (busy) return;
    const targets = items.filter((it) => it.productId && it.status !== 'done');
    if (!targets.length) { toast.error('Asigna al menos una imagen a un producto'); return; }
    setBusy(true);
    let ok = 0, fail = 0;
    for (const it of targets) {
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, status: 'uploading', error: '' } : x)));
      try {
        const base64 = it.preview.split(',')[1];
        const mime = ALLOWED.includes(it.file.type) ? it.file.type : 'image/jpeg';
        const { data: media } = await api.post('/seller/images', { content_base64: base64, mime_type: mime, filename: it.name });
        await api.post(`/platform/catalog/${it.productId}/image`, { image_url: media.url });
        ok += 1;
        setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, status: 'done' } : x)));
      } catch (e) {
        fail += 1;
        setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, status: 'error', error: e.response?.data?.detail || 'Error al guardar' } : x)));
      }
    }
    setBusy(false);
    if (ok) toast.success(`${ok} imagen(es) asignada(s)`);
    if (fail) toast.error(`${fail} no se pudieron asignar`);
    if (ok && onDone) onDone();
  };

  const statusBadge = (it) => {
    if (it.status === 'done') return <Badge variant="outline" className="border-brand-sage text-brand-sage" data-testid={`bulk-status-${it.id}`}><Check className="h-3 w-3" /> Listo</Badge>;
    if (it.status === 'uploading') return <Badge variant="outline" data-testid={`bulk-status-${it.id}`}><Loader2 className="h-3 w-3 animate-spin" /> Subiendo</Badge>;
    if (it.status === 'error') return <Badge variant="outline" className="border-destructive text-destructive" data-testid={`bulk-status-${it.id}`}><X className="h-3 w-3" /> Error</Badge>;
    return <Badge variant="outline" data-testid={`bulk-status-${it.id}`}>Pendiente</Badge>;
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="seller-dialog max-w-3xl" data-testid="bulk-image-dialog">
        <DialogHeader>
          <DialogTitle>Carga masiva de imágenes</DialogTitle>
          <DialogDescription>Arrastra varias fotos, empareja cada una con un producto del catálogo global y aplícalas en un solo paso. Solo se actualiza la imagen de la ficha; no cambia precios ni inventario.</DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter') inputRef.current?.click(); }}
          data-testid="bulk-image-dropzone"
          className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-muted/40 p-8 text-center cursor-pointer hover:border-brand-orange/60 transition-colors"
        >
          <UploadCloud className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium">Arrastra imágenes aquí o haz clic para seleccionar</p>
          <p className="text-xs text-muted-foreground">PNG, JPG, WEBP o GIF · hasta 8 MB cada una</p>
          <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" data-testid="bulk-image-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        </div>

        {items.length > 0 && (
          <div className="max-h-80 overflow-auto space-y-2 pr-1" data-testid="bulk-image-list">
            {items.map((it) => (
              <div key={it.id} className="flex items-center gap-3 rounded-lg border border-border p-2" data-testid={`bulk-image-row-${it.id}`}>
                <img src={it.preview} alt={it.name} className="h-14 w-14 rounded-md object-cover bg-muted shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs text-muted-foreground mb-1" title={it.name}>{it.name}</p>
                  <ProductPicker products={availableProducts} value={it.productId} onChange={(pid) => setProduct(it.id, pid)} testid={`bulk-picker-${it.id}`} />
                  {it.error && <p className="text-xs text-destructive mt-1">{it.error}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {statusBadge(it)}
                  <Button type="button" variant="ghost" size="icon" aria-label="Quitar" data-testid={`bulk-remove-${it.id}`} disabled={busy} onClick={() => removeItem(it.id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && matchedCount === 0 && (
          <Alert data-testid="bulk-image-hint"><ImageOff className="h-4 w-4" /><AlertDescription>Empareja al menos una imagen con un producto para poder aplicar los cambios.</AlertDescription></Alert>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} data-testid="bulk-image-cancel" onClick={onClose}>Cerrar</Button>
          <Button type="button" className="seller-primary" disabled={busy || matchedCount === 0} data-testid="bulk-image-apply" onClick={apply}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Subir y asignar {matchedCount > 0 ? `(${matchedCount})` : ''}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
